import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

export interface PackageSummary {
  id: string;
  name: string;
  subtitle: string;
  price: number | string;
  currency?: string;
  period?: string;
}

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  pkg: PackageSummary;
  onSuccess?: (planId: string) => void;
  lang?: 'en' | 'km' | 'zh';
}

type PaymentMethodId = 'khqr' | 'card' | 'alipay' | 'wechat' | 'telegram';

export default function PaymentModal({ open, onClose, pkg, onSuccess, lang = 'en' }: PaymentModalProps) {
  const navigate = useNavigate();
  const [activeMethod, setActiveMethod] = useState<PaymentMethodId | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paidSuccess, setPaidSuccess] = useState(false);
  const [countdown, setCountdown] = useState(900); // 15:00 countdown for KHQR

  // Card form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');

  // Reset when opened
  useEffect(() => {
    if (open) {
      setActiveMethod(null);
      setPaidSuccess(false);
      setProcessing(false);
      setCountdown(900);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Timer for KHQR
  useEffect(() => {
    if (!open || activeMethod !== 'khqr' || paidSuccess) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [open, activeMethod, paidSuccess]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open && !processing) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, processing, onClose]);

  if (!open) return null;

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleCompletePayment = (methodName: string) => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setPaidSuccess(true);
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(pkg.id);
        } else {
          navigate(`/dashboard?plan=${pkg.id}&payment=${methodName}&paid=1`);
        }
      }, 1500);
    }, 1200);
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
      {/* Dark Blurred Backdrop */}
      <div
        className="fixed inset-0 bg-neutral-950/70 backdrop-blur-sm transition-opacity"
        onClick={() => !processing && onClose()}
        aria-hidden="true"
      />

      {/* Modal Dialog Card */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-neutral-200/90 overflow-hidden z-10 transition-all">
        {/* ============================================================== */}
        {/* MODAL HEADER                                                   */}
        {/* ============================================================== */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-2">
            {activeMethod && !paidSuccess && (
              <button
                type="button"
                onClick={() => setActiveMethod(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer mr-1"
                aria-label="Back to methods"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
            )}
            <h3 className="font-heading font-bold text-lg md:text-xl text-neutral-900">
              {paidSuccess ? 'Payment Confirmed' : activeMethod ? 'Complete Payment' : 'Payment'}
            </h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={processing}
            className="w-8 h-8 rounded-full flex items-center justify-center text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors cursor-pointer disabled:opacity-50"
            aria-label="Close payment dialog"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="px-6 pb-6 pt-1">
          {/* ============================================================== */}
          {/* PACKAGE SUMMARY CARD (Matches Image 1)                         */}
          {/* ============================================================== */}
          <div className="bg-neutral-50/90 border border-neutral-200/80 rounded-xl p-4 flex items-center justify-between">
            <div className="min-w-0 pr-3">
              <span className="text-[10px] font-bold text-neutral-400 tracking-wider uppercase block mb-0.5">
                PACKAGE
              </span>
              <h4 className="font-heading font-bold text-sm text-neutral-900 truncate">
                {pkg.name}
              </h4>
              <p className="text-xs text-neutral-500 truncate mt-0.5">
                {pkg.subtitle}
              </p>
            </div>

            <div className="shrink-0 text-right">
              <span className="font-heading font-black text-2xl text-neutral-900 font-mono">
                {pkg.currency || '$'}{pkg.price}
              </span>
            </div>
          </div>

          {/* ============================================================== */}
          {/* VIEW 1: METHOD SELECTION (Matches Image 1)                     */}
          {/* ============================================================== */}
          {!activeMethod && !paidSuccess && (
            <div className="mt-5">
              <p className="text-xs font-semibold text-neutral-800 mb-3 tracking-tight">
                Choose way to pay
              </p>

              <div className="flex flex-col gap-2.5">
                {/* 1. ABA KHQR */}
                <button
                  type="button"
                  onClick={() => setActiveMethod('khqr')}
                  className="w-full p-3 sm:p-3.5 rounded-xl border border-neutral-200 hover:border-primary-500 hover:bg-primary-50/20 transition-all flex items-center justify-between group cursor-pointer text-left bg-white shadow-2xs hover:shadow-xs"
                >
                  <div className="flex items-center gap-3">
                    {/* Branded ABA KHQR Badge */}
                    <div className="w-10 h-10 rounded-xl overflow-hidden flex flex-col shrink-0 shadow-2xs border border-neutral-200/80">
                      <div className="bg-[#002b49] flex-1 flex items-center justify-center text-[10px] font-black text-white tracking-wider">
                        ABA
                      </div>
                      <div className="bg-[#e41b23] h-3.5 flex items-center justify-center text-[7px] font-black text-white tracking-widest">
                        KHQR
                      </div>
                    </div>

                    <div>
                      <h5 className="font-bold text-sm text-neutral-900 group-hover:text-primary-600 transition-colors">
                        ABA KHQR
                      </h5>
                      <p className="text-xs text-neutral-500">
                        Scan to pay with any banking app
                      </p>
                    </div>
                  </div>

                  <span className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 group-hover:text-primary-600 group-hover:bg-primary-50 transition-all shrink-0">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </span>
                </button>

                {/* 2. Credit/Debit Card */}
                <button
                  type="button"
                  onClick={() => setActiveMethod('card')}
                  className="w-full p-3 sm:p-3.5 rounded-xl border border-neutral-200 hover:border-primary-500 hover:bg-primary-50/20 transition-all flex items-center justify-between group cursor-pointer text-left bg-white shadow-2xs hover:shadow-xs"
                >
                  <div className="flex items-center gap-3">
                    {/* Card Icon Badge */}
                    <div className="w-10 h-10 rounded-xl bg-[#0066cc] flex items-center justify-center text-white shrink-0 shadow-2xs">
                      <i className="ri-bank-card-fill text-xl"></i>
                    </div>

                    <div>
                      <h5 className="font-bold text-sm text-neutral-900 group-hover:text-primary-600 transition-colors">
                        Credit/Debit Card
                      </h5>
                      {/* Brand Logos */}
                      <div className="flex items-center gap-1.5 mt-1">
                        {/* Visa */}
                        <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-[#1a1f71] text-white text-[9px] font-black italic tracking-wider leading-none">
                          VISA
                        </span>
                        {/* Mastercard */}
                        <span className="inline-flex items-center gap-0 px-1 py-0.5 rounded bg-neutral-100 border border-neutral-200 leading-none">
                          <span className="w-2.5 h-2.5 rounded-full bg-[#eb001b] inline-block -mr-1"></span>
                          <span className="w-2.5 h-2.5 rounded-full bg-[#f79e1b] inline-block opacity-90"></span>
                        </span>
                        {/* UnionPay */}
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#007b83] text-white text-[8px] font-bold leading-none">
                          UnionPay
                        </span>
                        {/* JCB */}
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#003399] text-white text-[8px] font-bold leading-none">
                          JCB
                        </span>
                      </div>
                    </div>
                  </div>

                  <span className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 group-hover:text-primary-600 group-hover:bg-primary-50 transition-all shrink-0">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </span>
                </button>

                {/* 3. Alipay */}
                <button
                  type="button"
                  onClick={() => setActiveMethod('alipay')}
                  className="w-full p-3 sm:p-3.5 rounded-xl border border-neutral-200 hover:border-primary-500 hover:bg-primary-50/20 transition-all flex items-center justify-between group cursor-pointer text-left bg-white shadow-2xs hover:shadow-xs"
                >
                  <div className="flex items-center gap-3">
                    {/* Alipay Icon Badge */}
                    <div className="w-10 h-10 rounded-xl bg-[#1677ff] flex items-center justify-center text-white shrink-0 shadow-2xs font-bold text-xl">
                      支
                    </div>

                    <div>
                      <h5 className="font-bold text-sm text-neutral-900 group-hover:text-primary-600 transition-colors">
                        Alipay
                      </h5>
                      <p className="text-xs text-neutral-500">
                        Scan to pay with Alipay
                      </p>
                    </div>
                  </div>

                  <span className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 group-hover:text-primary-600 group-hover:bg-primary-50 transition-all shrink-0">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </span>
                </button>

                {/* 4. WeChat */}
                <button
                  type="button"
                  onClick={() => setActiveMethod('wechat')}
                  className="w-full p-3 sm:p-3.5 rounded-xl border border-neutral-200 hover:border-primary-500 hover:bg-primary-50/20 transition-all flex items-center justify-between group cursor-pointer text-left bg-white shadow-2xs hover:shadow-xs"
                >
                  <div className="flex items-center gap-3">
                    {/* WeChat Icon Badge */}
                    <div className="w-10 h-10 rounded-xl bg-[#07c160] flex items-center justify-center text-white shrink-0 shadow-2xs">
                      <i className="ri-wechat-fill text-2xl"></i>
                    </div>

                    <div>
                      <h5 className="font-bold text-sm text-neutral-900 group-hover:text-primary-600 transition-colors">
                        WeChat
                      </h5>
                      <p className="text-xs text-neutral-500">
                        Scan to pay with WeChat
                      </p>
                    </div>
                  </div>

                  <span className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 group-hover:text-primary-600 group-hover:bg-primary-50 transition-all shrink-0">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </span>
                </button>

                {/* 5. Telegram */}
                <button
                  type="button"
                  onClick={() => setActiveMethod('telegram')}
                  className="w-full p-3 sm:p-3.5 rounded-xl border border-neutral-200 hover:border-primary-500 hover:bg-primary-50/20 transition-all flex items-center justify-between group cursor-pointer text-left bg-white shadow-2xs hover:shadow-xs"
                >
                  <div className="flex items-center gap-3">
                    {/* Telegram Icon Badge */}
                    <div className="w-10 h-10 rounded-xl bg-[#229ed9] flex items-center justify-center text-white shrink-0 shadow-2xs">
                      <i className="ri-telegram-fill text-2xl"></i>
                    </div>

                    <div>
                      <h5 className="font-bold text-sm text-neutral-900 group-hover:text-primary-600 transition-colors">
                        Telegram
                      </h5>
                      <p className="text-xs text-neutral-500">
                        Chat with our team to pay or ask questions
                      </p>
                    </div>
                  </div>

                  <span className="w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center text-neutral-400 group-hover:text-primary-600 group-hover:bg-primary-50 transition-all shrink-0">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </span>
                </button>
              </div>

              {/* Bottom Footer Actions (Matches Image 1) */}
              <div className="mt-5 pt-4 border-t border-neutral-100 flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-neutral-500 hover:text-neutral-800 transition-colors cursor-pointer flex items-center gap-1 font-medium"
                >
                  <i className="ri-arrow-left-s-line text-sm"></i>
                  <span>Edit name and dates</span>
                </button>

                <div className="flex items-center gap-1.5 text-neutral-400 text-xs">
                  <i className="ri-lock-2-line text-xs"></i>
                  <span>Secure payment</span>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* VIEW 2A: ABA KHQR SCANNER VIEW                                 */}
          {/* ============================================================== */}
          {activeMethod === 'khqr' && !paidSuccess && (
            <div className="mt-4 flex flex-col items-center text-center animate-fade-in">
              <div className="w-full bg-red-50/80 border border-red-200/80 rounded-xl p-3 mb-4 flex items-center justify-between text-xs text-red-700">
                <span className="font-semibold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  Expires in {formatTimer(countdown)}
                </span>
                <span className="font-mono font-bold">${pkg.price} USD</span>
              </div>

              {/* KHQR Card Box */}
              <div className="relative p-4 bg-white border-2 border-red-500 rounded-2xl shadow-lg w-64 flex flex-col items-center">
                {/* Bakong / KHQR Branded Header */}
                <div className="w-full flex items-center justify-between pb-2 mb-2 border-b border-neutral-200">
                  <div className="text-left leading-tight">
                    <p className="font-black text-xs text-red-600 tracking-wider">KHQR</p>
                    <p className="text-[9px] text-neutral-400 font-medium">Bakong Individual</p>
                  </div>
                  <span className="text-xs font-bold text-neutral-800">LoyaltyBridge</span>
                </div>

                {/* Simulated KHQR QR Code SVG */}
                <div className="relative w-48 h-48 bg-white p-2 rounded-lg border border-neutral-100 flex items-center justify-center">
                  <svg className="w-full h-full text-neutral-900" viewBox="0 0 100 100" fill="currentColor">
                    {/* Corner Position Detection Squares */}
                    <path d="M5 5h30v30H5V5zm5 5v20h20V10H10zm5 5h10v10H15V15z" />
                    <path d="M65 5h30v30H65V5zm5 5v20h20V10H70zm5 5h10v10H75V15z" />
                    <path d="M5 65h30v30H5V65zm5 5v20h20V70H10zm5 5h10v10H15V75z" />
                    {/* Synthetic QR Code Data Patterns */}
                    <rect x="42" y="10" width="6" height="6" />
                    <rect x="52" y="10" width="6" height="6" />
                    <rect x="42" y="22" width="6" height="6" />
                    <rect x="52" y="22" width="6" height="6" />
                    <rect x="10" y="42" width="6" height="6" />
                    <rect x="22" y="42" width="6" height="6" />
                    <rect x="10" y="52" width="6" height="6" />
                    <rect x="22" y="52" width="6" height="6" />
                    <rect x="42" y="42" width="16" height="16" />
                    <rect x="65" y="42" width="8" height="6" />
                    <rect x="80" y="42" width="12" height="6" />
                    <rect x="65" y="54" width="10" height="8" />
                    <rect x="80" y="54" width="8" height="8" />
                    <rect x="42" y="65" width="8" height="8" />
                    <rect x="55" y="65" width="6" height="12" />
                    <rect x="42" y="80" width="14" height="6" />
                    <rect x="65" y="72" width="12" height="6" />
                    <rect x="82" y="72" width="8" height="14" />
                    <rect x="68" y="84" width="10" height="6" />
                  </svg>

                  {/* KHQR Center Logo Badge */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center font-black text-[9px] shadow-sm border-2 border-white">
                      KHQR
                    </div>
                  </div>
                </div>

                <div className="w-full mt-2.5 pt-2 border-t border-neutral-100 flex items-center justify-between text-[10px] text-neutral-500">
                  <span>Merchant: LoyaltyBridge</span>
                  <span className="font-bold text-neutral-900">${pkg.price}</span>
                </div>
              </div>

              <p className="text-xs text-neutral-600 mt-3 max-w-xs">
                Scan with <strong>ABA Mobile</strong>, <strong>Bakong</strong>, or any banking app in Cambodia.
              </p>

              {/* Action Buttons */}
              <div className="w-full mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handleCompletePayment('ABA KHQR')}
                  disabled={processing}
                  className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-red-600 to-[#e41b23] hover:from-red-700 hover:to-red-800 text-white font-bold text-sm shadow-sm flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50"
                >
                  {processing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      <span>Verifying Payment...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-checkbox-circle-line text-lg"></i>
                      <span>Simulate Successful Payment</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveMethod(null)}
                  disabled={processing}
                  className="text-xs text-neutral-500 hover:text-neutral-800 py-1 transition-colors"
                >
                  ← Choose another payment method
                </button>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* VIEW 2B: CREDIT/DEBIT CARD FORM                                */}
          {/* ============================================================== */}
          {activeMethod === 'card' && !paidSuccess && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCompletePayment('Card');
              }}
              className="mt-4 flex flex-col gap-3.5 animate-fade-in"
            >
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1">
                  Cardholder Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. JORDAN BLAKE"
                  value={cardName}
                  onChange={(e) => setCardName(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none transition-all uppercase"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1">
                  Card Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    maxLength={19}
                    placeholder="4000 1234 5678 9010"
                    value={cardNumber}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 16);
                      const formatted = v.match(/.{1,4}/g)?.join(' ') || v;
                      setCardNumber(formatted);
                    }}
                    className="w-full pl-3.5 pr-10 py-2.5 rounded-xl border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none transition-all font-mono"
                  />
                  <i className="ri-bank-card-line absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 text-lg"></i>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1">
                    Expiration (MM/YY)
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={5}
                    placeholder="MM/YY"
                    value={cardExpiry}
                    onChange={(e) => {
                      let v = e.target.value.replace(/\D/g, '').slice(0, 4);
                      if (v.length > 2) v = `${v.slice(0, 2)}/${v.slice(2)}`;
                      setCardExpiry(v);
                    }}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none transition-all font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1">
                    Security Code (CVV)
                  </label>
                  <input
                    type="password"
                    required
                    maxLength={4}
                    placeholder="123"
                    value={cardCvc}
                    onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-neutral-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none transition-all font-mono"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={processing}
                className="w-full mt-2 py-3 px-4 rounded-xl bg-gradient-to-r from-primary-600 to-[#0094ff] hover:from-primary-700 hover:to-[#0080dd] text-white font-bold text-sm shadow-sm flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    <span>Processing Payment...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-lock-2-line text-base"></i>
                    <span>Pay ${pkg.price} USD</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveMethod(null)}
                disabled={processing}
                className="text-xs text-neutral-500 hover:text-neutral-800 text-center py-1 transition-colors"
              >
                ← Choose another payment method
              </button>
            </form>
          )}

          {/* ============================================================== */}
          {/* VIEW 2C: ALIPAY / WECHAT / TELEGRAM                            */}
          {/* ============================================================== */}
          {(activeMethod === 'alipay' || activeMethod === 'wechat' || activeMethod === 'telegram') && !paidSuccess && (
            <div className="mt-4 flex flex-col items-center text-center animate-fade-in">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 shadow-sm text-3xl text-white"
                style={{
                  backgroundColor:
                    activeMethod === 'alipay' ? '#1677ff' : activeMethod === 'wechat' ? '#07c160' : '#229ed9',
                }}
              >
                {activeMethod === 'alipay' && '支'}
                {activeMethod === 'wechat' && <i className="ri-wechat-fill"></i>}
                {activeMethod === 'telegram' && <i className="ri-telegram-fill"></i>}
              </div>

              <h4 className="font-heading font-bold text-base text-neutral-900">
                {activeMethod === 'alipay' && 'Pay with Alipay'}
                {activeMethod === 'wechat' && 'Pay with WeChat'}
                {activeMethod === 'telegram' && 'Connect via Telegram'}
              </h4>

              <p className="text-xs text-neutral-500 mt-1 max-w-xs leading-relaxed">
                {activeMethod === 'alipay' && 'Scan the QR code in your Alipay App to authorize payment.'}
                {activeMethod === 'wechat' && 'Scan with WeChat App to complete the payment for your subscription.'}
                {activeMethod === 'telegram' && 'Chat with our billing specialists directly on Telegram for instant activation and invoice.'}
              </p>

              {activeMethod !== 'telegram' ? (
                <div className="p-4 bg-white border border-neutral-200 rounded-xl shadow-xs my-4 w-44 h-44 flex items-center justify-center">
                  <div className="text-center">
                    <i className="ri-qr-code-line text-7xl text-neutral-800"></i>
                    <p className="text-[10px] text-neutral-400 mt-1">Scan with {activeMethod === 'alipay' ? 'Alipay' : 'WeChat'}</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-blue-50/80 border border-blue-200/80 rounded-xl my-4 text-xs text-blue-900 w-full text-left">
                  <p className="font-semibold flex items-center gap-1.5 mb-1 text-blue-800">
                    <i className="ri-customer-service-2-line text-sm"></i>
                    Direct Telegram Concierge
                  </p>
                  <p className="text-neutral-600">Username: <strong>@LoyaltyBridgeSupport</strong></p>
                  <p className="text-neutral-600 mt-0.5">Reference: <code>LB-{pkg.id.toUpperCase()}-{pkg.price}</code></p>
                </div>
              )}

              <div className="w-full flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => handleCompletePayment(activeMethod)}
                  disabled={processing}
                  className="w-full py-2.5 px-4 rounded-xl text-white font-bold text-sm shadow-sm flex items-center justify-center gap-2 cursor-pointer transition-all disabled:opacity-50"
                  style={{
                    backgroundColor:
                      activeMethod === 'alipay' ? '#1677ff' : activeMethod === 'wechat' ? '#07c160' : '#229ed9',
                  }}
                >
                  {processing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg"></i>
                      <span>Confirming...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line text-lg"></i>
                      <span>Confirm {activeMethod.toUpperCase()} Payment (${pkg.price})</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveMethod(null)}
                  disabled={processing}
                  className="text-xs text-neutral-500 hover:text-neutral-800 py-1 transition-colors"
                >
                  ← Choose another payment method
                </button>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* VIEW 3: SUCCESS CONFIRMATION                                   */}
          {/* ============================================================== */}
          {paidSuccess && (
            <div className="py-6 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
                <i className="ri-checkbox-circle-fill text-4xl"></i>
              </div>

              <h4 className="font-heading font-bold text-xl text-neutral-900">
                Payment Successful!
              </h4>
              <p className="text-sm text-neutral-500 mt-1 max-w-xs">
                Your subscription to <strong>{pkg.name}</strong> is now active. Redirecting to your dashboard...
              </p>

              <div className="mt-4 px-3 py-1.5 rounded-full bg-neutral-100 text-xs font-mono text-neutral-600">
                Amount Paid: ${pkg.price} USD
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

